import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import {
  analyzeWebsite,
  generateTopicIdeas,
  researchAndQueueTopics,
  generatePostFromTopic,
  approvePost,
  rejectPost,
  publishScheduledPosts,
  type BlogTopic,
  type BlogPost,
  type AutomationSettings
} from '../lib/blog-automation';

export default function BlogAutomationPage() {
  const [activeTab, setActiveTab] = useState<'settings' | 'topics' | 'queue' | 'published' | 'analytics'>('settings');
  const [settings, setSettings] = useState<AutomationSettings>({
    auto_mode: false,
    posts_per_day: 1,
    preferred_posting_time: '10:00',
    enable_image_generation: true,
    target_sites: ['halesac', 'myccs', 'abrahamac'], // All companies now have cities configured
    min_local_relevance: 75,
    research_frequency: 'weekly'
  });
  const [topics, setTopics] = useState<BlogTopic[]>([]);
  const [pendingPosts, setPendingPosts] = useState<BlogPost[]>([]);
  const [publishedPosts, setPublishedPosts] = useState<BlogPost[]>([]);
  const [isResearching, setIsResearching] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);

  // Load settings and data on mount
  useEffect(() => {
    loadSettings();
    loadTopics();
    loadPosts();
  }, []);

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_automation_settings')
        .select('*')
        .single();

      if (data && !error) {
        setSettings({
          auto_mode: data.auto_mode,
          posts_per_day: data.posts_per_day,
          preferred_posting_time: data.preferred_posting_time,
          enable_image_generation: data.enable_image_generation,
          target_sites: data.target_sites,
          min_local_relevance: data.min_local_relevance,
          research_frequency: data.research_frequency
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTopics = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_automation_topics')
        .select('*')
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });

      if (data && !error) {
        setTopics(data);
      }
    } catch (error) {
      console.error('Error loading topics:', error);
    }
  };

  const loadPosts = async () => {
    try {
      // Load pending posts
      const { data: pending } = await supabase
        .from('marketing_blog_posts')
        .select('*')
        .in('status', ['pending_approval', 'draft', 'scheduled'])
        .order('created_at', { ascending: false });

      if (pending) setPendingPosts(pending);

      // Load published posts
      const { data: published } = await supabase
        .from('marketing_blog_posts')
        .select('*')
        .eq('status', 'published')
        .order('published_at', { ascending: false })
        .limit(50);

      if (published) setPublishedPosts(published);
    } catch (error) {
      console.error('Error loading posts:', error);
    }
  };

  // ============================================================================
  // SETTINGS MANAGEMENT
  // ============================================================================

  const saveSettings = async () => {
    try {
      const { error } = await supabase
        .from('blog_automation_settings')
        .upsert({
          id: 1, // Single row for settings
          ...settings,
          updated_at: new Date().toISOString()
        });

      if (!error) {
        toast.success('Settings saved successfully!');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    }
  };

  // ============================================================================
  // TOPIC RESEARCH
  // ============================================================================

  const handleResearchTopics = async () => {
    setIsResearching(true);
    const results: { site: string; success: boolean; error?: string }[] = [];
    
    try {
      for (const site of settings.target_sites) {
        try {
          await researchAndQueueTopics(site, settings.min_local_relevance);
          results.push({ site, success: true });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          console.error(`Error researching topics for ${site}:`, error);
          results.push({ site, success: false, error: errorMessage });
        }
      }
      
      await loadTopics();
      
      // Show results summary
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;
      
      if (successCount > 0 && failCount === 0) {
        toast.success('Topic research completed! Check the Topics tab to review.');
        setActiveTab('topics');
      } else if (successCount > 0 && failCount > 0) {
        toast.success(`Research completed for ${successCount} site(s). ${failCount} site(s) had errors - check console for details.`, { duration: 5000 });
        setActiveTab('topics');
      } else {
        // All failed
        const errorDetails = results
          .filter(r => !r.success)
          .map(r => `${r.site}: ${r.error}`)
          .join('\n');
        toast.error(`All sites failed research:\n${errorDetails}`, { duration: 7000 });
      }
    } catch (error) {
      console.error('Error in research process:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(`Failed to research topics: ${errorMessage}`);
    } finally {
      setIsResearching(false);
    }
  };

  const handleGenerateFromTopic = async (topicId: string) => {
    setIsGenerating(true);
    try {
      const postId = await generatePostFromTopic(topicId, settings);
      
      // Immediately reload data
      await loadTopics();
      await loadPosts();
      
      // Show success message with image generation info
      if (settings.enable_image_generation) {
        toast.success('Post generated! 🎨 Featured image is being generated in the background (30-120 seconds).', {
          duration: 5000,
        });
      } else {
        toast.success('Post generated successfully! Check the Content Queue tab.');
      }
      
      // Navigate to queue to show the new post
      setActiveTab('queue');
    } catch (error) {
      console.error('Error generating post:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to generate post: ${errorMessage}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeleteTopic = async (topicId: string) => {
    if (!confirm('Are you sure you want to delete this topic?')) return;

    try {
      const { error } = await supabase
        .from('blog_automation_topics')
        .delete()
        .eq('id', topicId);

      if (error) throw error;

      await loadTopics();
      toast.success('Topic deleted successfully');
    } catch (error) {
      console.error('Error deleting topic:', error);
      toast.error('Failed to delete topic');
    }
  };

  const handleDeleteAllTopics = async () => {
    if (!confirm('Are you sure you want to delete ALL topics? This action cannot be undone.')) return;
    if (!confirm('Really delete all topics? This will remove all topic research data.')) return;

    try {
      const { error } = await supabase
        .from('blog_automation_topics')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all

      if (error) throw error;

      await loadTopics();
      toast.success('All topics deleted successfully');
    } catch (error) {
      console.error('Error deleting all topics:', error);
      toast.error('Failed to delete all topics');
    }
  };

  // ============================================================================
  // APPROVAL WORKFLOW
  // ============================================================================

  const handleApprove = async (postId: string) => {
    try {
      await approvePost(postId);
      await loadPosts();
      setSelectedPost(null);
      toast.success('Post approved and scheduled for publishing!');
    } catch (error) {
      console.error('Error approving post:', error);
      toast.error('Failed to approve post');
    }
  };

  const handleReject = async (postId: string) => {
    const reason = prompt('Reason for rejection:');
    if (!reason) return;

    try {
      await rejectPost(postId, reason);
      await loadPosts();
      setSelectedPost(null);
      toast.success('Post rejected');
    } catch (error) {
      console.error('Error rejecting post:', error);
      toast.error('Failed to reject post');
    }
  };

  // ============================================================================
  // ANALYTICS
  // ============================================================================

  const analytics = {
    totalTopics: topics.length,
    queuedTopics: topics.filter(t => t.status === 'queued').length,
    pendingReview: pendingPosts.filter(p => p.status === 'pending_approval').length,
    scheduled: pendingPosts.filter(p => p.status === 'scheduled').length,
    published: publishedPosts.length,
    avgLocalRelevance: topics.length > 0 
      ? Math.round(topics.reduce((sum, t) => sum + t.local_relevance, 0) / topics.length)
      : 0
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading blog automation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Blog Automation</h1>
        <p className="text-gray-600 mt-2">AI-powered blog content generation and publishing</p>
      </div>

      {/* Status Banner */}
      <div className={`mb-6 p-4 rounded-lg ${settings.auto_mode ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">
              {settings.auto_mode ? '🤖 Fully Autonomous Mode' : '👤 Manual Approval Mode'}
            </h3>
            <p className="text-sm text-gray-600">
              {settings.auto_mode 
                ? 'Posts are automatically generated and published daily'
                : 'Posts require admin approval before publishing'}
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-gray-900">{analytics.published}</div>
            <div className="text-sm text-gray-600">Posts Published</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'settings', label: 'Settings', icon: '⚙️' },
            { id: 'topics', label: 'Topic Research', icon: '🔍', badge: analytics.queuedTopics },
            { id: 'queue', label: 'Content Queue', icon: '📝', badge: analytics.pendingReview },
            { id: 'published', label: 'Published', icon: '✅', badge: analytics.published },
            { id: 'analytics', label: 'Analytics', icon: '📊' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>{tab.icon}</span>
              {tab.label}
              {tab.badge !== undefined && tab.badge > 0 && (
                <span className="ml-2 bg-blue-100 text-blue-600 py-0.5 px-2 rounded-full text-xs font-semibold">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'settings' && (
          <SettingsTab settings={settings} setSettings={setSettings} onSave={saveSettings} />
        )}
        {activeTab === 'topics' && (
          <TopicsTab 
            topics={topics} 
            onResearch={handleResearchTopics}
            onGenerate={handleGenerateFromTopic}
            onDeleteTopic={handleDeleteTopic}
            onDeleteAllTopics={handleDeleteAllTopics}
            isResearching={isResearching}
            isGenerating={isGenerating}
          />
        )}
        {activeTab === 'queue' && (
          <QueueTab 
            posts={pendingPosts}
            selectedPost={selectedPost}
            setSelectedPost={setSelectedPost}
            onApprove={handleApprove}
            onReject={handleReject}
            autoMode={settings.auto_mode}
          />
        )}
        {activeTab === 'published' && (
          <PublishedTab posts={publishedPosts} />
        )}
        {activeTab === 'analytics' && (
          <AnalyticsTab 
            analytics={analytics}
            topics={topics}
            posts={[...pendingPosts, ...publishedPosts]}
          />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// SETTINGS TAB
// ============================================================================

function SettingsTab({ settings, setSettings, onSave }: {
  settings: AutomationSettings;
  setSettings: (settings: AutomationSettings) => void;
  onSave: () => void;
}) {
  return (
    <div className="space-y-6">
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">Automation Settings</h2>
        
        {/* Auto Mode Toggle */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <div className="font-medium text-gray-900">Fully Autonomous Mode</div>
              <div className="text-sm text-gray-600">
                {settings.auto_mode 
                  ? 'Posts are automatically approved and published'
                  : 'Posts require manual approval before publishing'}
              </div>
            </div>
            <div className="relative">
              <input
                type="checkbox"
                checked={settings.auto_mode}
                onChange={(e) => setSettings({ ...settings, auto_mode: e.target.checked })}
                className="sr-only"
              />
              <div className={`w-14 h-8 rounded-full transition-colors ${settings.auto_mode ? 'bg-green-600' : 'bg-gray-300'}`}>
                <div className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform m-1 ${settings.auto_mode ? 'translate-x-6' : 'translate-x-0'}`}></div>
              </div>
            </div>
          </label>
        </div>

        {/* Target Sites */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Target Websites
          </label>
          <div className="space-y-2">
            {['halesac', 'myccs', 'abrahamac'].map(site => (
              <label key={site} className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.target_sites.includes(site)}
                  onChange={(e) => {
                    const newSites = e.target.checked
                      ? [...settings.target_sites, site]
                      : settings.target_sites.filter(s => s !== site);
                    setSettings({ ...settings, target_sites: newSites });
                  }}
                  className="mr-2"
                />
                <span className="text-sm">{site}.com</span>
              </label>
            ))}
          </div>
        </div>

        {/* Posts Per Day */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Posts Per Day
          </label>
          <input
            type="number"
            min="1"
            max="10"
            value={settings.posts_per_day}
            onChange={(e) => setSettings({ ...settings, posts_per_day: parseInt(e.target.value) })}
            className="input w-32"
          />
        </div>

        {/* Posting Time */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Preferred Posting Time
          </label>
          <input
            type="time"
            value={settings.preferred_posting_time}
            onChange={(e) => setSettings({ ...settings, preferred_posting_time: e.target.value })}
            className="input w-32"
          />
        </div>

        {/* Image Generation */}
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.enable_image_generation}
              onChange={(e) => setSettings({ ...settings, enable_image_generation: e.target.checked })}
              className="mr-2"
            />
            <span className="text-sm font-medium text-gray-700">
              Enable Midjourney Image Generation
            </span>
          </label>
        </div>

        {/* Min Local Relevance */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Minimum Local Relevance Score: {settings.min_local_relevance}%
          </label>
          <input
            type="range"
            min="0"
            max="100"
            value={settings.min_local_relevance}
            onChange={(e) => setSettings({ ...settings, min_local_relevance: parseInt(e.target.value) })}
            className="w-full"
          />
        </div>

        {/* Research Frequency */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Topic Research Frequency
          </label>
          <select
            value={settings.research_frequency}
            onChange={(e) => setSettings({ ...settings, research_frequency: e.target.value as any })}
            className="input"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>

        <button onClick={onSave} className="btn-primary">
          Save Settings
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// TOPICS TAB
// ============================================================================

function TopicsTab({ topics, onResearch, onGenerate, onDeleteTopic, onDeleteAllTopics, isResearching, isGenerating }: {
  topics: BlogTopic[];
  onResearch: () => void;
  onGenerate: (topicId: string) => void;
  onDeleteTopic: (topicId: string) => void;
  onDeleteAllTopics: () => void;
  isResearching: boolean;
  isGenerating: boolean;
}) {
  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold">AI Topic Research</h2>
            <p className="text-sm text-gray-600">Generate high-impact local SEO topics</p>
          </div>
          <div className="flex gap-2">
            {topics.length > 0 && (
              <button
                onClick={onDeleteAllTopics}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                🗑️ Delete All Topics
              </button>
            )}
            <button
              onClick={onResearch}
              disabled={isResearching}
              className="btn-primary"
            >
              {isResearching ? '🔄 Researching...' : '🔍 Research New Topics'}
            </button>
          </div>
        </div>
      </div>

      {topics.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-500 mb-4">No topics yet. Click "Research New Topics" to get started!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {topics.map((topic) => (
            <div key={topic.id} className="card hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-2">{topic.topic}</h3>
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                    <span className="flex items-center gap-1">
                      🎯 <strong>{topic.keyword}</strong>
                    </span>
                    <span>📊 {topic.search_volume} searches/mo</span>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      topic.competition === 'low' ? 'bg-green-100 text-green-800' :
                      topic.competition === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {topic.competition} competition
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Local Relevance:</span>
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${topic.local_relevance}%` }}
                        ></div>
                      </div>
                      <span className="font-semibold">{topic.local_relevance}%</span>
                    </div>
                    <span className="text-gray-600">Priority: {topic.priority}/10</span>
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                      {topic.target_site}.com
                    </span>
                  </div>
                </div>
                <div className="ml-4 flex gap-2">
                  {topic.status === 'queued' && (
                    <button
                      onClick={() => onGenerate(topic.id)}
                      disabled={isGenerating}
                      className="btn-secondary text-sm"
                    >
                      Generate Post
                    </button>
                  )}
                  {topic.status === 'generating' && (
                    <span className="text-sm text-blue-600 font-medium">⏳ Generating...</span>
                  )}
                  {topic.status === 'published' && (
                    <span className="text-sm text-green-600 font-medium">✅ Published</span>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteTopic(topic.id);
                    }}
                    className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors text-sm"
                    title="Delete topic"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// QUEUE TAB
// ============================================================================

function QueueTab({ posts, selectedPost, setSelectedPost, onApprove, onReject, autoMode }: {
  posts: BlogPost[];
  selectedPost: BlogPost | null;
  setSelectedPost: (post: BlogPost | null) => void;
  onApprove: (postId: string) => void;
  onReject: (postId: string) => void;
  autoMode: boolean;
}) {
  if (posts.length === 0) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-500 mb-4">No posts in queue. Generate posts from the Topics tab!</p>
      </div>
    );
  }

  if (selectedPost) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setSelectedPost(null)}
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            ← Back to Queue
          </button>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{selectedPost.title}</h2>
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded font-medium">
                  {selectedPost.target_site}.com
                </span>
                <span className={`px-2 py-1 rounded font-medium ${
                  selectedPost.status === 'pending_approval' ? 'bg-yellow-50 text-yellow-700' :
                  selectedPost.status === 'scheduled' ? 'bg-green-50 text-green-700' :
                  'bg-gray-50 text-gray-700'
                }`}>
                  {selectedPost.status}
                </span>
              </div>
            </div>
            {selectedPost.status === 'pending_approval' && (
              <div className="flex gap-2">
                <button
                  onClick={() => onReject(selectedPost.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Reject
                </button>
                <button
                  onClick={() => onApprove(selectedPost.id)}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Approve & Schedule
                </button>
              </div>
            )}
          </div>

          <div className="mb-4">
            <h3 className="font-semibold text-gray-900 mb-2">Excerpt</h3>
            <p className="text-gray-700">{selectedPost.excerpt}</p>
          </div>

          <div className="mb-4">
            <h3 className="font-semibold text-gray-900 mb-2">SEO Metadata</h3>
            <div className="bg-gray-50 p-3 rounded space-y-2">
              <div>
                <span className="text-sm font-medium text-gray-600">SEO Title:</span>
                <p className="text-sm">{selectedPost.seo_title}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">SEO Description:</span>
                <p className="text-sm">{selectedPost.seo_description}</p>
              </div>
              {selectedPost.keywords && selectedPost.keywords.length > 0 && (
                <div>
                  <span className="text-sm font-medium text-gray-600">Keywords:</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedPost.keywords.map((keyword, i) => (
                      <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {selectedPost.featured_image_url && (
            <div className="mb-4">
              <h3 className="font-semibold text-gray-900 mb-2">Featured Image</h3>
              <img 
                src={selectedPost.featured_image_url} 
                alt={selectedPost.title}
                className="w-full max-w-2xl rounded-lg shadow-md"
              />
            </div>
          )}

          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Content</h3>
            <div 
              className="prose max-w-none bg-gray-50 p-6 rounded"
              dangerouslySetInnerHTML={{ __html: selectedPost.content }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!autoMode && posts.some(p => p.status === 'pending_approval') && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800 font-medium">
            ⚠️ {posts.filter(p => p.status === 'pending_approval').length} post(s) awaiting your approval
          </p>
        </div>
      )}

      {posts.map((post) => (
        <div 
          key={post.id} 
          className="card hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => setSelectedPost(post)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">{post.title}</h3>
              <p className="text-sm text-gray-600 mb-3">{post.excerpt}</p>
              <div className="flex items-center gap-3 text-sm flex-wrap">
                <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded font-medium">
                  {post.target_site}.com
                </span>
                <span className={`px-2 py-1 rounded font-medium ${
                  post.status === 'pending_approval' ? 'bg-yellow-50 text-yellow-700' :
                  post.status === 'scheduled' ? 'bg-green-50 text-green-700' :
                  'bg-gray-50 text-gray-700'
                }`}>
                  {post.status}
                </span>
                {/* Image generation status indicator */}
                {post.status === 'draft' && !post.featured_image_url && (
                  <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded font-medium flex items-center gap-1">
                    <span className="animate-spin">🎨</span> Image Generating...
                  </span>
                )}
                {post.featured_image_url && post.status !== 'published' && (
                  <span className="px-2 py-1 bg-green-50 text-green-700 rounded font-medium">
                    ✅ Image Ready
                  </span>
                )}
                {post.scheduled_for && (
                  <span className="text-gray-600">
                    📅 {new Date(post.scheduled_for).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
            <button className="text-blue-600 hover:text-blue-800 font-medium">
              Review →
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// PUBLISHED TAB
// ============================================================================

function PublishedTab({ posts }: { posts: BlogPost[] }) {
  if (posts.length === 0) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-500">No published posts yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {posts.map((post) => (
        <div key={post.id} className="card hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">{post.title}</h3>
              <p className="text-sm text-gray-600 mb-3">{post.excerpt}</p>
              <div className="flex items-center gap-3 text-sm">
                <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded font-medium">
                  {post.target_site}.com
                </span>
                <span className="text-gray-600">
                  ✅ Published {new Date(post.published_at!).toLocaleDateString()}
                </span>
                {post.wordpress_url && (
                  <a 
                    href={post.wordpress_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    View Live →
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// ANALYTICS TAB
// ============================================================================

function AnalyticsTab({ analytics, topics, posts }: {
  analytics: {
    totalTopics: number;
    queuedTopics: number;
    pendingReview: number;
    scheduled: number;
    published: number;
    avgLocalRelevance: number;
  };
  topics: BlogTopic[];
  posts: BlogPost[];
}) {
  const topicsBySite = {
    halesac: topics.filter(t => t.target_site === 'halesac').length,
    myccs: topics.filter(t => t.target_site === 'myccs').length,
    abrahamac: topics.filter(t => t.target_site === 'abrahamac').length
  };

  const postsBySite = {
    halesac: posts.filter(p => p.target_site === 'halesac').length,
    myccs: posts.filter(p => p.target_site === 'myccs').length,
    abrahamac: posts.filter(p => p.target_site === 'abrahamac').length
  };

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <h3 className="text-sm font-medium text-gray-600 mb-1">Total Topics</h3>
          <div className="text-3xl font-bold text-gray-900">{analytics.totalTopics}</div>
          <p className="text-sm text-gray-500 mt-1">{analytics.queuedTopics} queued</p>
        </div>
        <div className="card">
          <h3 className="text-sm font-medium text-gray-600 mb-1">Content Queue</h3>
          <div className="text-3xl font-bold text-gray-900">{analytics.pendingReview}</div>
          <p className="text-sm text-gray-500 mt-1">{analytics.scheduled} scheduled</p>
        </div>
        <div className="card">
          <h3 className="text-sm font-medium text-gray-600 mb-1">Published Posts</h3>
          <div className="text-3xl font-bold text-gray-900">{analytics.published}</div>
          <p className="text-sm text-gray-500 mt-1">All time</p>
        </div>
      </div>

      {/* Site Breakdown */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">Content by Website</h2>
        <div className="space-y-4">
          {Object.entries(topicsBySite).map(([site, count]) => (
            <div key={site}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">{site}.com</span>
                <span className="text-sm text-gray-600">
                  {count} topics · {postsBySite[site as keyof typeof postsBySite]} posts
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full"
                  style={{ width: `${analytics.totalTopics > 0 ? (count / analytics.totalTopics) * 100 : 0}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quality Metrics */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">Quality Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-medium text-gray-600 mb-2">Average Local Relevance</h3>
            <div className="flex items-center gap-4">
              <div className="flex-1 bg-gray-200 rounded-full h-4">
                <div
                  className="bg-green-600 h-4 rounded-full"
                  style={{ width: `${analytics.avgLocalRelevance}%` }}
                ></div>
              </div>
              <span className="text-2xl font-bold text-gray-900">{analytics.avgLocalRelevance}%</span>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-600 mb-2">Topic Quality Distribution</h3>
            <div className="space-y-2">
              {['high', 'medium', 'low'].map(comp => {
                const count = topics.filter(t => 
                  (comp === 'high' && t.local_relevance >= 85) ||
                  (comp === 'medium' && t.local_relevance >= 70 && t.local_relevance < 85) ||
                  (comp === 'low' && t.local_relevance < 70)
                ).length;
                return (
                  <div key={comp} className="flex items-center justify-between text-sm">
                    <span className="capitalize">{comp} Relevance</span>
                    <span className="font-semibold">{count} topics</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Top Performing Topics */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">Top Topics by SEO Potential</h2>
        <div className="space-y-3">
          {topics
            .sort((a, b) => b.estimated_traffic - a.estimated_traffic)
            .slice(0, 5)
            .map(topic => (
              <div key={topic.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{topic.topic}</div>
                  <div className="text-sm text-gray-600">{topic.keyword}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-blue-600">{topic.estimated_traffic} visits/mo</div>
                  <div className="text-xs text-gray-500">{topic.search_volume} searches</div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
